import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { LogOut, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";

const PendingPage = () => {
  const { logout, user, profile, refreshProfile } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [statusError, setStatusError] = useState<string | null>(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [hasRedirected, setHasRedirected] = useState(false);
  const [pollCount, setPollCount] = useState(0);
  const pollTimer = useRef<number | null>(null);

  const handleLogout = async () => {
    try {
      await logout();
      navigate("/login", { replace: true });
    } catch {
      toast({
        title: "Erro ao sair",
        description: "Tente novamente em instantes.",
        variant: "destructive",
      });
    }
  };

  const handleRefresh = async () => {
    setIsRefreshing(true);
    setStatusError(null);
    try {
      const updated = await refreshProfile();
      if (updated.approved && !hasRedirected) {
        setHasRedirected(true);
        navigate("/", { replace: true });
      }
    } catch (err) {
      setStatusError((err as Error)?.message ?? "Não foi possível carregar seu perfil.");
    } finally {
      setIsRefreshing(false);
    }
  };

  const emailLabel = useMemo(
    () => profile?.email ?? user?.email ?? "Usuário autenticado",
    [profile?.email, user?.email]
  );

  // Polling: a cada 5s, até 12 vezes (~1 min)
  useEffect(() => {
    if (hasRedirected) return;
    if (profile?.approved) {
      setHasRedirected(true);
      navigate("/", { replace: true });
      return;
    }
    if (pollCount >= 12) return;

    pollTimer.current = window.setTimeout(() => {
      setPollCount((c) => c + 1);
      handleRefresh();
    }, 5000);

    return () => {
      if (pollTimer.current) {
        clearTimeout(pollTimer.current);
      }
    };
  }, [pollCount, profile?.approved, hasRedirected, navigate]);

  return (
    <div className="min-h-screen bg-muted flex items-center justify-center px-4">
      <div className="w-full max-w-lg text-center space-y-6 rounded-xl bg-background/60 shadow-elegant p-8 border border-border/60">
        <div className="space-y-3">
          <h1 className="text-3xl font-bold text-foreground">Seu acesso está pendente</h1>
          <p className="text-muted-foreground">
            Seu acesso está pendente de aprovação. Você receberá liberação em breve.
          </p>
          <p className="text-xs text-muted-foreground">
            Conta: {emailLabel}
          </p>
          {statusError && (
            <p className="text-xs text-destructive">{statusError}</p>
          )}
        </div>

        <div className="flex flex-wrap justify-center gap-3">
          <Button variant="outline" onClick={handleRefresh} disabled={isRefreshing} className="gap-2">
            <RefreshCw className={`h-4 w-4 ${isRefreshing ? "animate-spin" : ""}`} />
            Recarregar status
          </Button>
          <Button variant="outline" onClick={handleLogout} className="gap-2">
            <LogOut className="h-4 w-4" />
            Sair
          </Button>
        </div>
      </div>
    </div>
  );
};

export default PendingPage;


